import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

/**
 * Interceptor to handle Cloudflare protection and add browser-like headers.
 * 
 * Cloudflare blocks requests that appear automated. This interceptor:
 * 1. Adds comprehensive browser-like headers to make requests appear legitimate
 * 2. Ensures cookies are sent with cross-origin requests
 * 3. Handles Cloudflare challenge responses (403/503)
 * 4. Implements retry logic for transient Cloudflare blocks
 * 
 * Note: Restricted headers (Origin, Referer, Sec-Fetch-*) are automatically set by the browser
 * and cannot be set manually. The browser will set these headers automatically based on the request context.
 */
@Injectable()
export class CloudflareInterceptor implements HttpInterceptor {
  private readonly maxRetries = 2;
  private readonly retryDelayMs = 1000;
  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Clone the request with Cloudflare-friendly headers
    const cloudflareReq = this.addCloudflareHeaders(req);

    return next.handle(cloudflareReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check if this is a Cloudflare block
        if (this.isCloudflareBlock(error)) {
          console.warn('🛡️ Cloudflare security block detected');
          console.warn('[Cloudflare] Request blocked by Cloudflare:', req.url);
          return this.handleCloudflareBlock(error, req);
        }
        return throwError(() => error);
      }),
      // Retry on specific Cloudflare errors with exponential backoff
      retry({
        count: this.maxRetries,
        delay: (error, retryCount) => {
          if (this.isRetryableCloudflareError(error)) {
            const delay = this.retryDelayMs * Math.pow(2, retryCount - 1);
            console.log(`[Cloudflare] Retrying request (attempt ${retryCount}) after ${delay}ms`);
            return timer(delay);
          }
          return throwError(() => error);
        }
      })
    );
  }

  /**
   * Adds comprehensive browser-like headers that Cloudflare expects from legitimate traffic.
   * This helps avoid triggering Cloudflare's bot detection and WAF rules.
   * 
   * Note: Restricted headers (Origin, Referer, Sec-Fetch-*) are automatically set by the browser
   * and cannot be set manually. Attempting to set them will result in "Refused to set unsafe header" warnings.
   */
  private addCloudflareHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> {
    // Build headers that mimic a real browser
    // Note: Origin, Referer, and Sec-Fetch-* headers are automatically set by the browser
    // and should NOT be set manually as they are restricted headers
    const headers: Record<string, string> = {
      // Accept header that browsers send
      'Accept': req.headers.get('Accept') ?? 'application/json, text/plain, */*',
      // Language preference
      'Accept-Language': 'en-US,en;q=0.9',
      // Cache control
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    };

    // Check if the request body is FormData - DON'T set Content-Type for FormData
    const isFormData = req.body instanceof FormData;
    
    // Only set Content-Type for non-FormData POST/PUT/PATCH requests
    if (!isFormData && !req.headers.has('Content-Type') && ['POST', 'PUT', 'PATCH'].includes(req.method)) {
      headers['Content-Type'] = 'application/json';
    }

    return req.clone({
      setHeaders: headers,
      withCredentials: true // Critical for Cloudflare cookie handling
    });
  }

  /**
   * Detects if an error response is from Cloudflare's security system.
   */
  private isCloudflareBlock(error: HttpErrorResponse): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    // Cloudflare typically returns 403 or 503 for challenges/blocks
    const cloudflareStatusCodes = [403, 503, 520, 521, 522, 523, 524];
    if (!cloudflareStatusCodes.includes(error.status)) {
      return false;
    }

    // Check for Cloudflare-specific indicators in the response
    const errorBody = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
    const cloudflareIndicators = [
      'cloudflare',
      'cf-ray',
      'cf_clearance',
      '__cf_bm',
      'security service',
      'you have been blocked',
      'ray id',
      'enable cookies'
    ];

    return cloudflareIndicators.some(indicator => 
      errorBody?.toLowerCase().includes(indicator) ||
      error.headers?.has('cf-ray') ||
      error.headers?.has('cf-cache-status')
    );
  }

  /**
   * Determines if the Cloudflare error is transient and worth retrying.
   */
  private isRetryableCloudflareError(error: HttpErrorResponse): boolean {
    // 503 Service Unavailable and 5xx errors from Cloudflare are often transient
    return error.status === 503 || 
           error.status === 520 || // Web server returned unknown error
           error.status === 521 || // Web server is down
           error.status === 522 || // Connection timed out
           error.status === 523 || // Origin unreachable
           error.status === 524;   // Timeout occurred
  }

  /**
   * Handles Cloudflare blocks with user-friendly error messages.
   */
  private handleCloudflareBlock(error: HttpErrorResponse, originalReq: HttpRequest<unknown>): Observable<never> {
    console.warn('[Cloudflare] Blocked request details:', {
      url: originalReq.url,
      method: originalReq.method,
      status: error.status,
      cfRay: error.headers?.get('cf-ray'),
      message: 'Request blocked by Cloudflare security'
    });

    // Create a more user-friendly error
    const cloudflareError = new HttpErrorResponse({
      error: {
        code: 'CLOUDFLARE_BLOCK',
        message: 'Request blocked by security service. Please try refreshing the page or clearing your browser cookies.',
        originalStatus: error.status,
        cfRay: error.headers?.get('cf-ray'),
        suggestion: 'If the issue persists, please contact support.'
      },
      status: error.status,
      statusText: 'Cloudflare Security Block',
      url: originalReq.url
    });

    return throwError(() => cloudflareError);
  }
}


